import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ensureEventId } from '@/hooks/useEvent';
import { toast } from 'sonner';

export interface WeddingTask {
  id: string;
  user_id: string;
  invitation_id: string | null;
  title: string;
  due_date: string | null;
  completed: boolean;
  created_at: string;
}

export interface CreateTaskInput {
  title: string;
  due_date?: string | null;
  invitation_id?: string | null;
}

export interface UpdateTaskInput {
  title?: string;
  due_date?: string | null;
  completed?: boolean;
}

export const DEFAULT_TASK_TITLES = [
  'Reservar salón',
  'Definir invitados',
  'Elegir catering',
  'Comprar vestido/traje',
  'Comprar anillos',
];

async function getCurrentUserId(): Promise<string> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('No autenticado');
  return userData.user.id;
}

export function useTasks() {
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: ['tasks', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wedding_tasks')
        .select('*')
        .order('completed', { ascending: true })
        .order('due_date', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as WeddingTask[];
    },
    enabled: !!userId,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateTaskInput) => {
      const userId = user?.id ?? (await getCurrentUserId());
      const eventId = await ensureEventId(userId);

      const { data, error } = await supabase
        .from('wedding_tasks')
        .insert({
          user_id: userId,
          event_id: eventId,
          title: input.title,
          due_date: input.due_date ?? null,
          invitation_id: input.invitation_id ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as WeddingTask;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', user?.id] });
    },
    onError: (error) => {
      toast.error('Error al crear la tarea: ' + error.message);
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTaskInput }) => {
      const { data: result, error } = await supabase
        .from('wedding_tasks')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result as WeddingTask;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', user?.id] });
    },
    onError: (error) => {
      toast.error('Error al actualizar la tarea: ' + error.message);
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { error } = await supabase
        .from('wedding_tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', user?.id] });
      toast.success('Tarea eliminada');
    },
    onError: (error) => {
      toast.error('Error al eliminar la tarea: ' + error.message);
    },
  });
}
